import { createContext, memo, useContext, useMemo } from "react";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import {
  AvatarStatus,
  RedoAvatar,
  RedoAvatarProps,
  StatusDotColor,
} from "../../avatar/redo-avatar";
import { Tooltip } from "../../tooltip/tooltip";
import * as navigationCss from "../redo-navigation.module.css";

export interface NavigationUserProfile {
  name: string;
  teamName: string;
  status?: StatusDotColor;
  profileImageUrl: string | null;
  profileDropdownOpen: boolean;
  setProfileDropdownOpen: (open: boolean) => void;
  setDropdownAnchor: (anchor: HTMLElement | null) => void;
}

export const UserProfileContext = createContext<
  NavigationUserProfile | undefined
>(undefined);

export const UserProfile = memo(function UserProfile({
  collapsed,
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  collapsed: boolean;
}) {
  const userProfile = useContext(UserProfileContext);

  const avatar: RedoAvatarProps | undefined = useMemo(() => {
    if (!userProfile) {
      return undefined;
    }

    const { name, profileImageUrl, status } = userProfile;

    const avatarStatus: AvatarStatus | undefined = status
      ? { type: "presence", color: status }
      : undefined;

    return {
      content: {
        type: "image",
        imageUrl: profileImageUrl,
        altText: name,
        backup: { type: "text", text: name },
      },
      status: avatarStatus,
      size: "xs",
    };
  }, [userProfile]);

  if (!userProfile || !avatar) {
    return null;
  }

  const {
    name,
    teamName,
    profileDropdownOpen,
    setProfileDropdownOpen,
    setDropdownAnchor,
  } = userProfile;

  if (!collapsed) {
    return (
      <Flex px="xl" py="xl">
        <Flex
          as="button"
          borderRadius="md"
          className={navigationCss.navProfile}
          data-dd-action-name={actionName}
          gap="md"
          grow={1}
          onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
          px="md"
          py="sm"
          ref={setDropdownAnchor}
        >
          <RedoAvatar {...avatar} />
          <Flex dir="column" gap="none">
            <Text fontSize="xs" fontWeight="regular" textColor="primary">
              {name}
            </Text>

            <Text fontSize="xs" fontWeight="regular" textColor="secondary">
              {teamName}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    );
  } else {
    return (
      <Tooltip darkMode placement="right" title={name}>
        <button
          className={navigationCss.userProfileItem}
          data-dd-action-name={actionName}
          onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
          ref={setDropdownAnchor}
          type="button"
        >
          <RedoAvatar {...avatar} />
        </button>
      </Tooltip>
    );
  }
});
